import { invoiceTotal } from "./calc";
import type { Invoice, SavedInvoiceSummary } from "./types";

const DB_NAME = "nvoice";
const DB_VERSION = 1;
const DRAFT_KEY = "current";
const INVOICES_STORE = "invoices";
const DRAFTS_STORE = "drafts";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(INVOICES_STORE))
        db.createObjectStore(INVOICES_STORE, { keyPath: "id" });
      if (!db.objectStoreNames.contains(DRAFTS_STORE)) db.createObjectStore(DRAFTS_STORE);
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function tx<T>(
  storeName: string,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
) {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const request = run(store);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

export async function saveDraft(invoice: Invoice) {
  await tx(DRAFTS_STORE, "readwrite", (store) =>
    store.put({ ...invoice, updatedAt: new Date().toISOString() }, DRAFT_KEY),
  );
}

export async function getDraft() {
  return tx<Invoice | undefined>(DRAFTS_STORE, "readonly", (store) => store.get(DRAFT_KEY));
}

export async function saveInvoice(invoice: Invoice) {
  const updated: Invoice = { ...invoice, updatedAt: new Date().toISOString() };
  await tx(INVOICES_STORE, "readwrite", (store) => store.put(updated));
  await saveDraft(updated);
  return updated;
}

export async function getInvoice(id: string) {
  return tx<Invoice | undefined>(INVOICES_STORE, "readonly", (store) => store.get(id));
}

export async function deleteInvoice(id: string) {
  await tx(INVOICES_STORE, "readwrite", (store) => store.delete(id));
}

export async function listInvoices(): Promise<SavedInvoiceSummary[]> {
  const invoices = await tx<Invoice[]>(INVOICES_STORE, "readonly", (store) => store.getAll());
  return invoices
    .map((invoice) => ({
      id: invoice.id,
      invoiceNumber: invoice.details.invoiceNumber,
      clientName: invoice.client.name,
      total: invoiceTotal(invoice),
      updatedAt: invoice.updatedAt,
    }))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}
